import React from 'react';
import { 
  TableCell, 
  TableHead, 
  TableRow, 
  TableSortLabel,
  Box,
  useTheme,
  alpha
} from '@mui/material';

// Define column keys for sorting
export type SortKey = 'id' | 'name' | 'lineOfBusiness' | 'enabled' | 'supersetRole' | 'lastUpdated';

// Table Header Component
const AccountsTableHeader: React.FC<{
  sortBy: SortKey;
  sortDirection: 'asc' | 'desc';
  handleSort: (key: SortKey) => void;
}> = ({ sortBy, sortDirection, handleSort }) => {
  const theme = useTheme();
  
  const headerCellStyle = {
    fontWeight: 600, 
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    textTransform: 'uppercase',
    letterSpacing: '0.025em',
    py: 1.5,
    borderBottom: `2px solid ${alpha(theme.palette.divider, 0.7)}`,
    backgroundColor: theme.palette.background.default,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.04)
    }
  };
  
  return (
    <TableHead>
      <TableRow>
        <TableCell 
          sx={{ 
            ...headerCellStyle, 
            display: { xs: 'none', md: 'table-cell' }
          }}
        >
          <TableSortLabel
            active={sortBy === 'id'}
            direction={sortBy === 'id' ? sortDirection : 'asc'}
            onClick={() => handleSort('id')}
            sx={{ 
              '& .MuiTableSortLabel-icon': {
                color: sortBy === 'id' ? `${theme.palette.primary.main} !important` : undefined
              }
            }}
          >
            ID
          </TableSortLabel>
        </TableCell>
        <TableCell sx={headerCellStyle}>
          <TableSortLabel
            active={sortBy === 'name'}
            direction={sortBy === 'name' ? sortDirection : 'asc'}
            onClick={() => handleSort('name')}
            sx={{
              '& .MuiTableSortLabel-icon': {
                color: sortBy === 'name' ? `${theme.palette.primary.main} !important` : undefined
              }
            }}
          >
            Account Name
          </TableSortLabel>
        </TableCell>
        <TableCell sx={headerCellStyle}>
          <TableSortLabel
            active={sortBy === 'lineOfBusiness'}
            direction={sortBy === 'lineOfBusiness' ? sortDirection : 'asc'}
            onClick={() => handleSort('lineOfBusiness')}
            sx={{
              '& .MuiTableSortLabel-icon': {
                color: sortBy === 'lineOfBusiness' ? `${theme.palette.primary.main} !important` : undefined
              }
            }}
          >
            Line Of Business
          </TableSortLabel>
        </TableCell>
        <TableCell align="center" sx={headerCellStyle}>
          <TableSortLabel
            active={sortBy === 'enabled'}
            direction={sortBy === 'enabled' ? sortDirection : 'asc'}
            onClick={() => handleSort('enabled')}
            sx={{
              '& .MuiTableSortLabel-icon': {
                color: sortBy === 'enabled' ? `${theme.palette.primary.main} !important` : undefined
              }
            }}
          >
            <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
              Strategy Portal
              <Box component="span" sx={{ display: 'block', fontSize: '0.75rem', mt: 0.3 }}>
                Status
              </Box>
            </Box>
          </TableSortLabel>
        </TableCell>
        <TableCell sx={headerCellStyle}>
          <TableSortLabel
            active={sortBy === 'supersetRole'}
            direction={sortBy === 'supersetRole' ? sortDirection : 'asc'}
            onClick={() => handleSort('supersetRole')}
            sx={{
              '& .MuiTableSortLabel-icon': {
                color: sortBy === 'supersetRole' ? `${theme.palette.primary.main} !important` : undefined
              }
            }}
          >
            Superset Role
          </TableSortLabel>
        </TableCell>
        <TableCell sx={headerCellStyle}>
          <TableSortLabel
            active={sortBy === 'lastUpdated'}
            direction={sortBy === 'lastUpdated' ? sortDirection : 'asc'}
            onClick={() => handleSort('lastUpdated')}
            sx={{
              '& .MuiTableSortLabel-icon': {
                color: sortBy === 'lastUpdated' ? `${theme.palette.primary.main} !important` : undefined
              }
            }}
          >
            Last Updated
          </TableSortLabel>
        </TableCell>
        <TableCell align="center" sx={headerCellStyle}>Actions</TableCell>
      </TableRow>
    </TableHead>
  );
};

export default AccountsTableHeader;